import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Lock, ArrowRight, AlertCircle, CheckCircle, User, Shield, Mail, KeyRound } from "lucide-react";
import api, { clearAuthToken, setAuthToken } from "@/lib/api";

// Valid access codes
const VALID_ACCESS_CODES = ["LPAMINDWAVE"];

// Admin emails that can access without code
const ADMIN_EMAILS = ["piccinini.ps@gmail.com"];

const LoginPage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState("choice"); // "choice" | "code" | "email-login" | "email-register"
  const [accessCode, setAccessCode] = useState("");
  const [codeValid, setCodeValid] = useState(false);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  // Email/password fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [name, setName] = useState("");

  // Clear any old session ONLY if user navigated here explicitly (not from redirect loop)
  useEffect(() => {
    const isExplicitLogout = new URLSearchParams(window.location.search).get('logout') === 'true';
    if (isExplicitLogout) {
      document.cookie = "session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      clearAuthToken();
    }
  }, []);

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    setChecking(true);
    setError("");

    setTimeout(() => {
      const upperCode = accessCode.trim().toUpperCase();
      
      if (VALID_ACCESS_CODES.includes(upperCode)) {
        setCodeValid(true);
        localStorage.setItem('access_code', upperCode);
      } else {
        setError("Codice non valido. Verifica il codice e riprova.");
      }
      setChecking(false);
    }, 500);
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Email + password login
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError("");
    setChecking(true);
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedPassword = password.trim();
    // Retry once if first attempt is a network/timeout error (preview cold-start)
    const attemptLogin = async () => api.post('/auth/login-password', { email: trimmedEmail, password: trimmedPassword }, { timeout: 25000 });
    try {
      let data;
      try {
        ({ data } = await attemptLogin());
      } catch (firstErr) {
        // If it's a network or 5xx error, server might be cold-starting → wait + retry once
        const status = firstErr.response?.status;
        if (!status || status >= 500) {
          await new Promise((r) => setTimeout(r, 3000));
          ({ data } = await attemptLogin());
        } else {
          throw firstErr;
        }
      }
      if (data.session_token) {
        setAuthToken(data.session_token);
      }
      const isAdmin = ADMIN_EMAILS.includes(data.email);
      let target = "/oggi";
      if (!isAdmin) {
        try {
          const sub = await api.get('/stripe/subscription-status');
          if (!sub.data.is_active) target = "/richiedi-accesso";
        } catch {
          target = "/richiedi-accesso";
        }
      }
      window.location.href = target;
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg;
      if (typeof detail === "string") {
        msg = detail;
      } else if (err.response?.status) {
        msg = `Errore server (${err.response.status}). Riprova tra un attimo.`;
      } else {
        msg = "Server non raggiungibile. Riprova tra 30 secondi (può essere addormentato).";
      }
      setError(msg);
      setChecking(false);
    }
  };

  // Email + password registration with promo code
  const handleEmailRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("La password deve avere almeno 8 caratteri.");
      return;
    }
    if (password !== passwordConfirm) {
      setError("Le due password non coincidono.");
      return;
    }
    setChecking(true);
    try {
      const code = (localStorage.getItem('access_code') || accessCode || "").trim().toUpperCase();
      const { data } = await api.post('/auth/register', {
        email: email.trim().toLowerCase(),
        password,
        name: name.trim() || undefined,
        access_code: code || undefined,
      });
      if (data.session_token) {
        setAuthToken(data.session_token);
      }
      localStorage.removeItem('access_code');
      window.location.href = "/oggi";
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Registrazione fallita. Riprova.");
      setChecking(false);
    }
  };

  // Choice view - first time or returning user
  if (mode === "choice") {
    return (
      <div className="min-h-screen bg-white flex flex-col" data-testid="login-choice-revela">
        <header className="p-6 sm:p-8">
          <div className="container mx-auto max-w-7xl flex items-center justify-between">
            <h1 className="font-display text-3xl sm:text-4xl text-gold leading-none">
              Revela
            </h1>
            <img
              src="/logo-transparent.png"
              alt="Laboratorio di Psicologia Applicata"
              className="h-9 sm:h-10 object-contain opacity-80"
            />
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            <div className="text-center mb-10">
              <p className="text-xs sm:text-sm text-gold font-medium tracking-[0.18em] uppercase mb-3">
                Bentornato
              </p>
              <h2 className="font-display text-4xl sm:text-5xl text-slate-800 font-light leading-tight mb-3">
                Accedi a Revela
              </h2>
              <p className="text-base text-slate-500 font-light italic">
                Scegli come vuoi entrare.
              </p>
            </div>

            <div className="space-y-3">
              {/* PRIMARY: Email + password - most reliable on PWA / iOS */}
              <Button
                onClick={() => { setError(""); setMode("email-login"); }}
                className="w-full h-14 text-base font-light tracking-wide rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white hover:shadow-lg transition-shadow"
                data-testid="login-email-password-btn"
              >
                <Mail className="w-4 h-4 mr-3" strokeWidth={1.5} />
                Accedi con email e password
              </Button>

              {/* New user with code */}
              <Button
                variant="outline"
                onClick={() => setMode("code")}
                className="w-full h-14 text-base font-light tracking-wide rounded-full border border-amber-700/40 text-slate-700 hover:bg-amber-50/50 hover:border-amber-700/60 transition-colors"
                data-testid="login-with-code-btn"
              >
                <Lock className="w-4 h-4 mr-3" strokeWidth={1.5} />
                Ho un codice di accesso
              </Button>

              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase tracking-[0.18em]">
                  <span className="px-4 bg-white text-slate-400 font-medium">oppure</span>
                </div>
              </div>

              {/* Google login - secondary on PWA (less reliable on iOS) */}
              <Button
                variant="ghost"
                onClick={handleGoogleLogin}
                className="w-full h-12 text-sm font-light tracking-wide text-slate-600 hover:bg-slate-50 hover:text-slate-800 rounded-full"
                data-testid="login-google-btn"
              >
                <User className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Accedi con Google
              </Button>
            </div>

            <div className="mt-10 pt-6 border-t border-slate-100 text-center">
              {/* Privacy Banner */}
              <div className="bg-emerald-50/60 border border-emerald-200/60 rounded-lg p-3 mb-5 flex items-center justify-center gap-2 flex-wrap">
                <Shield className="w-4 h-4 text-emerald-700" strokeWidth={1.5} />
                <span className="text-xs text-emerald-800 font-light">
                  <strong className="font-medium">I tuoi dati sono privati</strong> — solo tu puoi vederli
                </span>
                <Link to="/privacy" className="text-xs text-emerald-700 hover:text-emerald-900 underline decoration-emerald-300 underline-offset-2 ml-1">
                  Info
                </Link>
              </div>

              <p className="text-xs text-slate-500 font-light italic leading-relaxed">
                Il codice di accesso viene fornito ai partecipanti dei corsi di <strong className="font-medium not-italic text-slate-700">Laura Piccinini</strong>.
              </p>
              <div className="flex justify-center gap-5 mt-4 text-xs">
                <a
                  href="https://www.laurapiccinini.it"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-600 hover:text-slate-800 underline decoration-slate-300 hover:decoration-slate-500 underline-offset-2 transition-colors font-light"
                >
                  laurapiccinini.it
                </a>
                <a
                  href="https://www.psicoapplicata.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-600 hover:text-slate-800 underline decoration-slate-300 hover:decoration-slate-500 underline-offset-2 transition-colors font-light"
                >
                  psicoapplicata.org
                </a>
              </div>

              <div className="mt-6 pt-4 border-t border-slate-100">
                <Link to="/" className="text-xs text-slate-400 hover:text-slate-600 font-light tracking-wide transition-colors">
                  ← Torna alla pagina principale
                </Link>
              </div>
            </div>
          </div>
        </main>

        <footer className="p-6 text-center">
          <p className="text-[11px] text-slate-400 font-light tracking-wide">
            Revela © {new Date().getFullYear()} — Laura Piccinini
          </p>
        </footer>
      </div>
    );
  }

  // Email + password login view
  if (mode === "email-login") {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <header className="p-6">
          <div className="container mx-auto max-w-7xl flex items-center justify-between">
            <h1 className="font-display text-3xl sm:text-4xl text-gold leading-none">
              Revela
            </h1>
            <img src="/logo-transparent.png" alt="Laboratorio di Psicologia Applicata" className="h-10 object-contain" />
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center p-6">
          <Card className="w-full max-w-md border-0 shadow-xl">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-50 to-amber-100/70 border border-amber-700/20 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-7 h-7 text-gold" strokeWidth={1.5} />
                </div>
                <h2 className="text-2xl font-bold mb-2">Accedi</h2>
                <p className="text-muted-foreground">Inserisci email e password.</p>
              </div>

              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Email</label>
                  <Input
                    type="email"
                    autoComplete="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder="tu@esempio.it"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(""); }}
                    className="h-12"
                    autoFocus
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Password</label>
                  <Input
                    type="password"
                    autoComplete="current-password"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(""); }}
                    className="h-12"
                    data-testid="login-password-input"
                  />
                </div>
                {error && (
                  <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg" data-testid="login-error">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
                <Button
                  type="submit"
                  className="w-full h-12 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white font-light tracking-wide hover:shadow-lg transition-shadow"
                  disabled={!email.trim() || !password || checking}
                  data-testid="login-submit-btn"
                >
                  {checking ? "Accesso in corso..." : "Accedi"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>

              <button
                onClick={() => { setMode("choice"); setError(""); setPassword(""); }}
                className="w-full mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="login-back-btn"
              >
                ← Torna indietro
              </button>

              <p className="mt-6 text-center text-xs text-muted-foreground">
                Non hai ancora un account?{" "}
                <button
                  onClick={() => setMode("code")}
                  className="text-slate-700 hover:text-slate-900 underline decoration-amber-700/40 hover:decoration-amber-700/70 underline-offset-2 font-medium"
                >
                  Registrati con un codice di accesso
                </button>
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Email + password registration view (after code is verified)
  if (mode === "email-register") {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <header className="p-6">
          <div className="container mx-auto max-w-7xl flex items-center justify-between">
            <h1 className="font-display text-3xl sm:text-4xl text-gold leading-none">
              Revela
            </h1>
            <img src="/logo-transparent.png" alt="Laboratorio di Psicologia Applicata" className="h-10 object-contain" />
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center p-6">
          <Card className="w-full max-w-md border-0 shadow-xl">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-50 to-amber-100/70 border border-amber-700/20 flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="w-7 h-7 text-gold" strokeWidth={1.5} />
                </div>
                <h2 className="text-2xl font-bold mb-2">Crea account</h2>
                <p className="text-muted-foreground">Codice {accessCode || localStorage.getItem('access_code')} attivato.</p>
              </div>

              <form onSubmit={handleEmailRegister} className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Nome</label>
                  <Input
                    type="text"
                    placeholder="Come vuoi essere chiamato/a"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="h-12"
                    data-testid="register-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Email</label>
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder="tu@esempio.it"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(""); }}
                    className="h-12"
                    data-testid="register-email-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Password (min 8 caratteri)</label>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(""); }}
                    className="h-12"
                    data-testid="register-password-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Conferma password</label>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    placeholder="••••••••"
                    value={passwordConfirm}
                    onChange={(e) => { setPasswordConfirm(e.target.value); setError(""); }}
                    className="h-12"
                    data-testid="register-password-confirm-input"
                  />
                </div>
                {error && (
                  <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg" data-testid="register-error">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
                <Button
                  type="submit"
                  className="w-full h-12 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white font-light tracking-wide hover:shadow-lg transition-shadow"
                  disabled={!email.trim() || !password || !passwordConfirm || checking}
                  data-testid="register-submit-btn"
                >
                  {checking ? "Creazione account..." : "Crea account"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>

              <button
                onClick={() => { setMode("code"); setError(""); }}
                className="w-full mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                ← Torna indietro
              </button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Code entry view
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="p-6 sm:p-8">
        <div className="container mx-auto max-w-7xl flex items-center justify-between">
          <h1 className="font-display text-3xl sm:text-4xl text-gold leading-none">
            Revela
          </h1>
          <img 
            src="/logo-transparent.png"
            alt="Laboratorio di Psicologia Applicata"
            className="h-9 sm:h-10 object-contain opacity-80"
          />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardContent className="p-8">
            
            {!codeValid ? (
              <>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-50 to-amber-100/70 border border-amber-700/20 flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-7 h-7 text-gold" strokeWidth={1.5} />
                  </div>
                  <h2 className="text-2xl font-bold mb-2">Inserisci il codice</h2>
                  <p className="text-muted-foreground">
                    Inserisci il codice di accesso che hai ricevuto.
                  </p>
                </div>

                <form onSubmit={handleCodeSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Codice di accesso
                    </label>
                    <Input
                      type="text"
                      placeholder="Inserisci il codice"
                      value={accessCode}
                      onChange={(e) => {
                        setAccessCode(e.target.value.toUpperCase());
                        setError("");
                      }}
                      className="text-center text-lg tracking-widest uppercase h-12"
                      autoFocus
                    />
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <Button 
                    type="submit"
                    className="w-full h-12 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white font-light tracking-wide hover:shadow-lg transition-shadow"
                    disabled={!accessCode.trim() || checking}
                  >
                    {checking ? "Verifica in corso..." : "Verifica codice"}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>

                <button
                  onClick={() => setMode("choice")}
                  className="w-full mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  ← Torna indietro
                </button>
              </>
            ) : (
              <>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-50 to-amber-100/70 border border-amber-700/20 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-7 h-7 text-gold" strokeWidth={1.5} />
                  </div>
                  <p className="text-xs text-gold font-medium tracking-[0.18em] uppercase mb-2">Codice verificato</p>
                  <h2 className="font-display text-3xl sm:text-4xl text-slate-800 font-light leading-tight mb-2">
                    Crea il tuo account
                  </h2>
                  <p className="text-sm text-slate-500 font-light italic">
                    Bastano pochi secondi per iniziare.
                  </p>
                </div>

                <div className="bg-amber-50/50 border border-amber-700/20 rounded-lg p-4 mb-6 text-center">
                  <p className="text-sm text-slate-700 font-light">
                    Il tuo codice <strong className="font-medium">{accessCode}</strong> include l'accesso completo a tutti i percorsi guidati.
                  </p>
                </div>

                <Button
                  onClick={() => { setError(""); setMode("email-register"); }}
                  className="w-full h-14 text-base font-light tracking-wide rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white hover:shadow-lg transition-shadow"
                  data-testid="register-email-password-btn"
                >
                  <Mail className="w-4 h-4 mr-3" strokeWidth={1.5} />
                  Crea account con email e password
                </Button>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase tracking-[0.18em]">
                    <span className="px-4 bg-white text-slate-400 font-medium">oppure</span>
                  </div>
                </div>

                <Button
                  variant="ghost"
                  onClick={handleGoogleLogin}
                  className="w-full h-12 text-sm font-light tracking-wide text-slate-600 hover:bg-slate-50 hover:text-slate-800 rounded-full"
                  data-testid="register-google-btn"
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Accedi con Google
                </Button>

                <button
                  onClick={() => {
                    setCodeValid(false);
                    setAccessCode("");
                    localStorage.removeItem('access_code');
                  }}
                  className="w-full mt-4 text-xs text-slate-400 hover:text-slate-600 font-light tracking-wide transition-colors"
                >
                  ← Usa un altro codice
                </button>
              </>
            )}
          </CardContent>
        </Card>
      </main>

      <footer className="p-6 text-center">
        <div className="bg-emerald-50/60 border border-emerald-200/60 rounded-full inline-flex items-center gap-2 px-4 py-2 mb-2">
          <Shield className="w-3.5 h-3.5 text-emerald-700" strokeWidth={1.5} />
          <Link to="/privacy" className="text-xs text-emerald-800 hover:text-emerald-900 font-light">
            I tuoi dati sono privati e solo tuoi — Leggi la Privacy Policy
          </Link>
        </div>
        <p className="text-[11px] text-slate-400 font-light tracking-wide">
          Revela © {new Date().getFullYear()} — Laura Piccinini
        </p>
      </footer>
    </div>
  );
};

export default LoginPage;
